import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../layout/Container";
import { SectionTitle } from "../ui/SectionTitle";
import { AnimatedSection } from "../ui/AnimatedSection";
import { AboutCard } from "./AboutCard";
import { ServiceCard } from "./ServiceCard";
import { TeamMemberCard } from "./TeamMemberCard";
import { TeamMemberModal, type TeamMemberModalMember } from "./TeamMemberModal";
import { PartnerLogos } from "./PartnerLogos";
import { ContactMapGoogle } from "./ContactMapGoogle";
import { ASSETS, type AssetImageKey } from "../../constants/assets";

const ABOUT_CARD_KEYS = [
  "about.cards.expertise",
  "about.cards.ethics",
  "about.cards.precision",
  "about.cards.collaboration",
  "about.cards.communication",
  "about.cards.deadlines",
] as const;

const ABOUT_ICONS: { circle: AssetImageKey; icon: AssetImageKey }[] = [
  { circle: "ellipse", icon: "iconExpert" },
  { circle: "ellipse", icon: "iconScales" },
  { circle: "ellipse2", icon: "iconTarget" },
  { circle: "ellipse", icon: "iconHandshake" },
  { circle: "ellipse", icon: "iconCommunication" },
  { circle: "ellipse", icon: "iconDeadline" },
];

const SERVICE_KEYS = [
  "services.accounting",
  "services.tax",
  "services.payroll",
  "services.business",
] as const;

const TEAM_MEMBERS: {
  memberId: string;
  roleKey: string;
  imageSrc: string;
  imagePosition?: string;
}[] = [
  { memberId: "vasil", roleKey: "team.roleVasil", imageSrc: ASSETS.teamAvatar1 },
  {
    memberId: "simona",
    roleKey: "team.roleSimona",
    imageSrc: ASSETS.teamAvatar2,
    imagePosition: "center 25%",
  },
  {
    memberId: "zornitsa",
    roleKey: "team.roleZornitsa",
    imageSrc: ASSETS.teamAvatarZornitsa,
    imagePosition: "center 25%",
  },
  {
    memberId: "yanaMaria",
    roleKey: "team.roleExpert",
    imageSrc: ASSETS.teamAvatarYanaMarcheva,
    imagePosition: "center 30%",
  },
  {
    memberId: "gabriela",
    roleKey: "team.roleExpert",
    imageSrc: ASSETS.teamAvatar4,
    imagePosition: "center 25%",
  },
  {
    memberId: "vasilena",
    roleKey: "team.roleExpert",
    imageSrc: ASSETS.teamAvatarVasilenaToteva,
    imagePosition: "center 20%",
  },
  { memberId: "ginka", roleKey: "team.roleExpert", imageSrc: ASSETS.teamAvatarPlaceholder },
];

export function AboutSection(): React.ReactElement {
  const { t } = useTranslation();

  return (
    <section id="about-us" className="py-12 md:py-16">
      <AnimatedSection>
        <div className="flex flex-col items-center gap-10">
          <SectionTitle>{t("about.title")}</SectionTitle>
          <Container className="max-w-[1000px]">
            <p className="text-[#474242] text-2xl md:text-3xl font-extralight text-center leading-snug">
              {t("about.intro")}
            </p>
          </Container>
        </div>
        <div className="px-4 sm:px-6 lg:px-16 py-12 md:py-[120px]">
          <Container>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 list-none p-0 m-0 items-stretch">
              {ABOUT_CARD_KEYS.map((key, i) => (
                <li key={key} className="min-h-[384px]">
                  <AboutCard
                    title={t(`${key}.title`)}
                    description={t(`${key}.description`)}
                    circleSrc={ASSETS[ABOUT_ICONS[i]!.circle]}
                    iconSrc={ASSETS[ABOUT_ICONS[i]!.icon]}
                  />
                </li>
              ))}
            </ul>
          </Container>
        </div>
      </AnimatedSection>
    </section>
  );
}

export function ServicesSection(): React.ReactElement {
  const { t } = useTranslation();

  return (
    <section id="services" className="py-12 md:py-16">
      <AnimatedSection>
        <div className="flex flex-col items-center gap-10">
          <SectionTitle>{t("services.title")}</SectionTitle>
          <Container className="max-w-[742px]">
            <p className="text-[#474242] text-2xl md:text-3xl font-extralight text-center leading-snug">
              {t("services.intro")}
            </p>
          </Container>
        </div>
        <div className="px-4 sm:px-6 lg:px-16 py-10 md:py-20">
          <Container>
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-8 list-none p-0 m-0">
              {SERVICE_KEYS.slice(0, 2).map((key) => (
                <li key={key}>
                  <ServiceCard
                    title={t(`${key}.title`)}
                    description={t(`${key}.description`)}
                  />
                </li>
              ))}
            </ul>
          </Container>
        </div>
        <div className="px-4 sm:px-6 lg:px-16 py-10 md:py-20">
          <Container>
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-8 list-none p-0 m-0">
              {SERVICE_KEYS.slice(2, 4).map((key) => (
                <li key={key}>
                  <ServiceCard
                    title={t(`${key}.title`)}
                    description={t(`${key}.description`)}
                  />
                </li>
              ))}
            </ul>
          </Container>
        </div>
      </AnimatedSection>
    </section>
  );
}

export function TeamSection(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [modalMember, setModalMember] = useState<TeamMemberModalMember | null>(null);

  const openMember = (member: (typeof TEAM_MEMBERS)[number]) => {
    const base = `team.members.${member.memberId}`;
    setModalMember({
      name: t(`${base}.name`),
      role: t(member.roleKey),
      imageSrc: member.imageSrc,
      imagePosition: member.imagePosition,
      profileKey: i18n.exists(`${base}.body`) ? base : undefined,
    });
  };

  return (
    <section id="team" className="py-12 md:py-16">
      <AnimatedSection>
        <div className="flex flex-col items-center gap-10">
          <SectionTitle>{t("team.title")}</SectionTitle>
          <Container className="max-w-[742px]">
            <p className="text-[#474242] text-2xl md:text-3xl font-extralight text-center leading-snug">
              {t("team.intro")}
            </p>
          </Container>
        </div>
        <div className="px-4 sm:px-6 lg:px-16 py-12 md:py-[120px]">
          <Container>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 list-none p-0 m-0 justify-items-center">
              {TEAM_MEMBERS.map((member) => {
                const base = `team.members.${member.memberId}`;
                const displayName = t(`${base}.name`);
                return (
                  <li key={member.memberId} className="w-full max-w-[560px]">
                    <TeamMemberCard
                      name={displayName}
                      role={t(member.roleKey)}
                      imageSrc={member.imageSrc}
                      imagePosition={member.imagePosition}
                      onOpenProfile={() => openMember(member)}
                      viewProfileLabel={t("team.viewProfile", { name: displayName })}
                    />
                  </li>
                );
              })}
            </ul>
          </Container>
        </div>
      </AnimatedSection>
      <TeamMemberModal
        open={modalMember !== null}
        member={modalMember}
        onClose={() => setModalMember(null)}
      />
    </section>
  );
}

export function PartnersSection(): React.ReactElement {
  const { t } = useTranslation();

  return (
    <section className="py-12 md:py-16">
      <AnimatedSection>
        <div className="flex flex-col items-center gap-10">
          <SectionTitle>{t("partners.title")}</SectionTitle>
        </div>
        <Container className="py-12 md:py-[120px]">
          <p className="text-gray-body text-lg font-medium text-center max-w-4xl mx-auto leading-relaxed mb-12">
            {t("partners.p1")}
          </p>
          <p className="text-gray-body text-lg font-medium text-center max-w-4xl mx-auto leading-relaxed mb-12">
            {t("partners.p2")}
          </p>
          <p className="text-gray-body text-lg font-medium text-center max-w-4xl mx-auto leading-relaxed">
            {t("partners.p3")}
          </p>
        </Container>
        <PartnerLogos />
      </AnimatedSection>
    </section>
  );
}

export function FindUsSection(): React.ReactElement {
  const { t } = useTranslation();

  return (
    <section id="find-us" className="py-12 md:py-16 bg-[#f8f8f8]">
      <AnimatedSection>
        <div className="flex flex-col items-center gap-10">
          <SectionTitle>{t("findUs.title")}</SectionTitle>
        </div>
        <Container className="pt-6 pb-12 md:pb-16">
          <p className="text-gray-body text-lg font-medium text-center mb-8">
            {t("footer.address")}
          </p>
          <div className="max-w-2xl mx-auto">
            <ContactMapGoogle />
          </div>
        </Container>
      </AnimatedSection>
    </section>
  );
}
